import { useCallback, useEffect, useState } from 'react'
import { kanbify } from '../lib/kanbifyClient'
import { logWarn } from '../lib/logger'
import i18n from '../i18n'

/**
 * Fetch all active company memberships for a given user.
 * Returns: [{ company_id, role, joined_at, is_active, companies: { id, name, join_code, location } }, ...]
 *
 * Depends on Wave 2 migration `20260512130000_kanbify_v3_company_memberships.sql`.
 * Before that migration ships, returns an empty array silently (table missing).
 */
export function useMemberships(userId) {
    const [memberships, setMemberships] = useState([])
    const [loading, setLoading] = useState(false)

    const fetch = useCallback(async () => {
        if (!userId) {
            setMemberships([])
            return
        }
        setLoading(true)
        const { data, error } = await kanbify
            .from('company_memberships')
            .select('company_id, role, joined_at, is_active, companies(id, name, join_code, location)')
            .eq('user_id', userId)
            .eq('is_active', true)
            .order('joined_at', { ascending: true })
        setLoading(false)
        if (error) {
            // 42P01 = relation does not exist (pre-Wave-2 deploy)
            if (error.code !== '42P01') {
                logWarn('memberships.fetch_failed', { message: error.message, code: error.code })
            }
            setMemberships([])
            return
        }
        setMemberships(data || [])
    }, [userId])

    // eslint-disable-next-line react-hooks/set-state-in-effect -- standard data-fetch-on-mount pattern; fetch is memoized via useCallback
    useEffect(() => { fetch() }, [fetch])

    return { memberships, loading, refetch: fetch }
}

/**
 * Switch the user's active company. Calls RPC `switch_active_company`.
 * Returns: { success: boolean, error?: string }
 */
export async function switchActiveCompany(companyId) {
    const { data, error } = await kanbify.rpc('switch_active_company', { p_company_id: companyId })
    if (error) {
        // Pre-Wave-2: function missing → soft-fallback so UI doesn't crash
        if (error.code === '42883') {
            return { success: false, error: i18n.t('errors.switchCompanyUnavailable') }
        }
        return { success: false, error: error.message }
    }
    if (data && typeof data === 'object' && data.success === false) {
        return { success: false, error: data.error || i18n.t('errors.switchCompanyFailed') }
    }
    return { success: true, companyId }
}

/**
 * Create a new company and become its admin. RPC `create_company_with_admin`.
 * Returns: { success: boolean, companyId?: string, joinCode?: string, error?: string }
 */
export async function createCompanyWithAdmin(name, location = null) {
    const { data, error } = await kanbify.rpc('create_company_with_admin', {
        p_name: name,
        p_location: location,
    })
    if (error) {
        if (error.code === '42883') {
            return { success: false, error: i18n.t('errors.createCompanyUnavailable') }
        }
        return { success: false, error: error.message }
    }
    if (data && typeof data === 'object' && data.success === false) {
        return { success: false, error: data.error || i18n.t('errors.createCompanyFailed') }
    }
    return {
        success: true,
        companyId: data?.company_id,
        joinCode: data?.join_code,
    }
}
